/*
 * Arnav booking form intelligence (22 October 2026).
 * - Options follow the purpose: exclusive commissions are offered only where the design team
 *   works (Arnav Jayanagar at present); other purposes at either boutique or on WhatsApp.
 * - Times: only free times from the concierge diary; before a diary has opening hours, the client
 *   gives a preferred time (9 am to 7 pm) and the team confirms it.
 * - Details are filled in for clients we know (their Arnav account, or a conversation with the
 *   concierge, which shares no contact details); whether each filled detail was kept is recorded.
 * - Sending saves the request with the concierge, then opens WhatsApp with a short reference code
 *   (no personal details in the message). If the concierge cannot be reached, the original
 *   WhatsApp message is used instead, so a client can always book.
 * - The form's steps are recorded only with the visitor's analytics consent, never what they typed.
 */
(() => {
  if (window.ArnavBooking) return;

  const PURPOSE = {
    Jewellery: 'JEWELLERY',
    'Exclusive Commissions': 'COMMISSION',
    'Engagement & Wedding': 'ENGAGEMENT_WEDDING',
    'Care Services': 'CARE',
    Other: 'OTHER',
  };
  const MEETING = { 'Arnav Lavelle Road': 'LAVELLE_ROAD', 'Arnav Jayanagar': 'JAYANAGAR', WhatsApp: 'WHATSAPP' };
  const STEP_NAMES = { 1: 'purpose', 2: 'meeting', 3: 'time', 4: 'details' };

  const randomKey = () => {
    const bytes = new Uint8Array(18);
    (window.crypto || {}).getRandomValues ? window.crypto.getRandomValues(bytes) : bytes.forEach((_, i) => (bytes[i] = Math.random() * 256));
    return 'bk-' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  };
  const to24 = (label) => {
    const m = String(label || '').match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    const h = (Number(m[1]) % 12) + (m[3].toUpperCase() === 'PM' ? 12 : 0);
    return `${String(h).padStart(2, '0')}:${m[2]}`;
  };
  const to12 = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const dh = h % 12 === 0 ? 12 : h % 12;
    return `${String(dh).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
  };
  const dayLabel = (date) =>
    new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(
      new Date(`${date}T00:00:00Z`),
    );

  function analyticsAllowed() {
    try {
      const p = window.Shopify && window.Shopify.customerPrivacy;
      return Boolean(p && typeof p.analyticsProcessingAllowed === 'function' && p.analyticsProcessingAllowed());
    } catch (e) {
      return false;
    }
  }

  function init(container, form, hooks) {
    const api = String(form.dataset.conciergeApi || '').replace(/\/+$/, '');
    if (!/^https:\/\//.test(api) && !/^http:\/\/(localhost|127\.0\.0\.1)/.test(api)) return null;
    const sessionKey = randomKey();
    const field = (name) => form.elements.namedItem(`contact[${name}]`);
    const state = {
      config: null,
      slots: null,
      purpose: null,
      meeting: null,
      prefill: {},
      stepSince: Date.now(),
      from: 'direct',
      queue: [],
      timer: null,
    };

    // ---------------------------------------------------------------- tracking (consent only)
    const flush = () => {
      clearTimeout(state.timer);
      state.timer = null;
      if (!state.queue.length) return;
      if (!analyticsAllowed()) {
        state.queue.length = 0;
        return;
      }
      const events = state.queue.splice(0, 40);
      fetch(`${api}/v1/booking/events`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionKey, analyticsConsent: true, events }),
      }).catch(() => undefined);
    };
    const track = (name, properties) => {
      if (!analyticsAllowed()) return;
      state.queue.push({ name, at: new Date().toISOString(), properties: properties || {} });
      if (!state.timer) state.timer = setTimeout(flush, 2000);
    };
    window.addEventListener('pagehide', flush);

    // ---------------------------------------------------------------- where the client came from
    const params = new URLSearchParams(window.location.search);
    let referrerPath = '';
    try {
      referrerPath = document.referrer ? new URL(document.referrer).pathname : '';
    } catch (e) {
      referrerPath = '';
    }
    // The form on the Bespoke Commission or Heirloom Revitalisation page itself, or arriving from one.
    const commissionContext =
      /^(commission|bespoke|heirloom)/i.test(params.get('purpose') || '') ||
      /\/pages\/(bespoke-commission|heirloom)/.test(referrerPath) ||
      /\/pages\/(bespoke-commission|heirloom)/.test(window.location.pathname);
    if (params.get('product_handle') || params.get('handle')) state.from = 'product_page';
    else if (params.get('store_name')) state.from = 'find_in_store';
    else if (commissionContext) state.from = 'commission_page';

    // ---------------------------------------------------------------- choices
    const subjectCards = () => [...container.querySelectorAll('.appointment-subject-grid .appointment-subject-card')];
    const channelCards = () => [...container.querySelectorAll('.appointment-channel-option-card')];
    const typeCards = () => [...container.querySelectorAll('.appointment-channel-type-card')];

    const meetingsFor = (purpose) => {
      const c = state.config && state.config.purposes.find((p) => p.key === purpose);
      if (c) return c.meetings.map((m) => m.key);
      if (purpose === 'COMMISSION')
        return String(form.dataset.commissionStores || 'Arnav Jayanagar')
          .split(',')
          .map((s) => MEETING[s.trim()])
          .filter(Boolean);
      return ['LAVELLE_ROAD', 'JAYANAGAR', 'WHATSAPP'];
    };

    /** Shows only the ways to meet that exist for this purpose. */
    function applyPurpose(purpose) {
      state.purpose = purpose;
      const allowed = meetingsFor(purpose);
      for (const card of channelCards()) {
        const key = MEETING[card.dataset.value];
        const ok = !purpose || allowed.includes(key);
        card.hidden = !ok;
        if (!ok && card.classList.contains('is-selected')) {
          card.classList.remove('is-selected');
          field('Appointment Location / Method').value = '';
          state.meeting = null;
        }
      }
      for (const t of typeCards()) {
        const group = t.dataset.channelType === 'online' ? ['WHATSAPP'] : ['LAVELLE_ROAD', 'JAYANAGAR'];
        t.hidden = !group.some((k) => allowed.includes(k));
      }
      // One way to meet: choose it for the client.
      if (allowed.length === 1 && !state.meeting) {
        const only = channelCards().find((c) => MEETING[c.dataset.value] === allowed[0]);
        if (only) {
          const type = typeCards().find((t) => t.dataset.channelType === only.dataset.parentChannel);
          if (type) type.click();
          only.click();
        }
      }
    }

    // ---------------------------------------------------------------- free times
    const slotBox = container.querySelector('[data-slots]');
    const slotDate = container.querySelector('[data-slot-date]');
    const slotTime = container.querySelector('[data-slot-time]');
    const nativeRows = [...container.querySelectorAll('[data-native-time]')];

    function useNative(native) {
      for (const row of nativeRows) {
        row.hidden = !native;
        for (const el of row.querySelectorAll('input,select')) {
          el.disabled = !native;
          if (el.dataset.wasRequired === undefined) el.dataset.wasRequired = el.required ? '1' : '0';
          el.required = native && el.dataset.wasRequired === '1';
        }
      }
      if (slotBox) slotBox.hidden = native;
      if (slotDate) slotDate.required = !native;
      if (slotTime) slotTime.required = !native;
    }

    function fillTimes() {
      const day = state.slots && state.slots.days.find((d) => d.date === slotDate.value);
      slotTime.replaceChildren(new Option('', ''));
      for (const t of day ? day.times : []) slotTime.append(new Option(to12(t).replace(/^0/, ''), t));
    }

    async function loadSlots() {
      if (!state.purpose || !state.meeting || !slotBox) return;
      try {
        const res = await fetch(
          `${api}/v1/booking/slots?purpose=${state.purpose}&meeting=${state.meeting}&days=21`,
        );
        if (!res.ok) throw new Error('slots');
        state.slots = await res.json();
      } catch (e) {
        state.slots = null;
      }
      if (state.slots && state.slots.configured && state.slots.days.length) {
        slotDate.replaceChildren(new Option('', ''));
        for (const d of state.slots.days) slotDate.append(new Option(dayLabel(d.date), d.date));
        fillTimes();
        useNative(false);
      } else useNative(true);
    }
    if (slotDate) slotDate.addEventListener('change', fillTimes);

    // ---------------------------------------------------------------- prefill
    const remember = (name, source, value) => {
      if (value) state.prefill[name] = { source, value };
    };
    function setIfEmpty(name, value, source, key) {
      const el = field(name);
      if (!el || !value || el.value) return;
      el.value = value;
      remember(key, source, value);
    }
    function chooseSubject(purpose, source) {
      const card = subjectCards().find((c) => PURPOSE[c.dataset.value] === purpose);
      if (!card || subjectCards().some((c) => c.classList.contains('is-selected'))) return;
      card.click();
      remember('purpose', source, purpose);
    }
    function chooseMeeting(meeting, source) {
      const card = channelCards().find((c) => MEETING[c.dataset.value] === meeting && !c.hidden);
      if (!card || state.meeting) return;
      const type = typeCards().find((t) => t.dataset.channelType === card.dataset.parentChannel);
      if (type) type.click();
      card.click();
      remember('meeting', source, meeting);
    }

    // Signed in to their Arnav account.
    const c = form.dataset;
    setIfEmpty('First Name', c.customerFirst, 'account', 'firstName');
    setIfEmpty('Last Name', c.customerLast, 'account', 'lastName');
    setIfEmpty('email', c.customerEmail, 'account', 'email');
    setIfEmpty('City', c.customerCity, 'account', 'city');
    if (c.customerPhone) {
      const digits = c.customerPhone.replace(/[^\d+]/g, '');
      const cc = field('Country Code');
      const match = cc && [...cc.options].map((o) => o.value).find((v) => digits.startsWith(v));
      if (match) {
        cc.value = match;
        setIfEmpty('phone', digits.slice(match.length), 'account', 'phone');
      }
    }

    // ---------------------------------------------------------------- listeners
    container.addEventListener('click', (e) => {
      const subject = e.target.closest('.appointment-subject-grid .appointment-subject-card');
      if (subject) {
        const purpose = PURPOSE[subject.dataset.value];
        if (purpose !== state.purpose) {
          applyPurpose(purpose);
          track('booking_choice', { purpose });
        }
        return;
      }
      const option = e.target.closest('.appointment-channel-option-card');
      if (option) {
        const meeting = MEETING[option.dataset.value];
        if (meeting !== state.meeting) {
          state.meeting = meeting;
          track('booking_choice', { meeting });
          loadSlots();
        }
      }
    });

    // ---------------------------------------------------------------- start
    (async () => {
      try {
        const res = await fetch(`${api}/v1/booking/config`);
        if (res.ok) state.config = await res.json();
      } catch (e) {
        state.config = null;
      }
      if (state.from === 'product_page') chooseSubject('JEWELLERY', 'product_page');
      if (commissionContext) chooseSubject('COMMISSION', 'commission_page');
      // A client chatting with the concierge (the same browser): what they asked for, never contact details.
      try {
        const stored = JSON.parse(localStorage.getItem(`jc-concierge:${form.dataset.shop}`) || 'null');
        if (stored && stored.sessionToken && (!stored.expiresAt || Date.parse(stored.expiresAt) > Date.now())) {
          const res = await fetch(`${api}/v1/booking/prefill`, {
            headers: { Authorization: `Bearer ${stored.sessionToken}` },
          });
          if (res.ok) {
            const p = await res.json();
            if (state.from === 'direct') state.from = 'concierge';
            if (p.purpose) chooseSubject(p.purpose, 'concierge');
            if (p.firstName) setIfEmpty('First Name', p.firstName, 'concierge', 'firstName');
            if (p.lastName) setIfEmpty('Last Name', p.lastName, 'concierge', 'lastName');
            if (p.meeting) chooseMeeting(p.meeting, 'concierge');
            if (p.productHandle && !form.querySelector('[name="contact[Product Handle]"]').value)
              form.querySelector('[name="contact[Product Handle]"]').value = p.productHandle;
          }
        }
      } catch (e) {
        /* no chat in this browser */
      }
      const store = params.get('store_name');
      if (store) {
        const meeting = Object.entries(MEETING).find(([label]) => store.toLowerCase().includes(label.replace(/^Arnav /, '').toLowerCase()));
        if (meeting) remember('meeting', 'find_in_store', meeting[1]);
      }
      track('booking_form_opened', { from: state.from });
      track('booking_step_viewed', { step: 'purpose' });
    })();

    return {
      /** Records moving between steps (time spent, going back). */
      onStep(from, to) {
        const seconds = Math.round((Date.now() - state.stepSince) / 1000);
        state.stepSince = Date.now();
        if (to > from) track('booking_step_completed', { step: STEP_NAMES[from], seconds });
        else track('booking_step_back', { from: STEP_NAMES[from], to: STEP_NAMES[to] });
        track('booking_step_viewed', { step: STEP_NAMES[to] });
      },
      onInvalid(step) {
        track('booking_error', { step: STEP_NAMES[step], code: 'missing_or_invalid' });
      },
      /** Saves the request with the concierge, then opens WhatsApp with its reference code. */
      async submit(status, button, fallback) {
        const purpose = state.purpose || PURPOSE[field('Subject').value];
        const meeting = state.meeting || MEETING[field('Appointment Location / Method').value];
        const nativeTime = (dateName, timeName) => {
          const d = field(dateName) && field(dateName).value;
          const t = to24(field(timeName) && field(timeName).value);
          return d && t ? `${d}T${t}` : undefined;
        };
        const time =
          slotBox && !slotBox.hidden && slotDate.value && slotTime.value
            ? `${slotDate.value}T${slotTime.value}`
            : nativeTime('Appointment Date', 'Appointment Time');
        const cc = field('Country Code') ? field('Country Code').value : '+91';
        const number = String(field('phone').value || '').replace(/\D/g, '').replace(/^0+/, '');
        const prefill = {};
        const current = {
          purpose,
          meeting,
          firstName: field('First Name').value.trim(),
          lastName: field('Last Name').value.trim(),
          email: field('email').value.trim(),
          city: field('City').value.trim(),
          phone: String(field('phone').value || '').trim(),
        };
        for (const [k, p] of Object.entries(state.prefill)) prefill[k] = { source: p.source, kept: current[k] === p.value };
        const body = {
          purpose,
          meeting,
          time,
          ...(nativeTime('Alternative Date', 'Alternative Time') ? { alternativeTime: nativeTime('Alternative Date', 'Alternative Time') } : {}),
          message: field('Message').value.trim(),
          ...(form.querySelector('[name="contact[Product Handle]"]').value
            ? { productHandle: form.querySelector('[name="contact[Product Handle]"]').value }
            : {}),
          ...(field('Title').value ? { title: field('Title').value } : {}),
          firstName: current.firstName,
          lastName: current.lastName,
          phone: `${cc}${number}`,
          email: current.email,
          ...(current.city ? { city: current.city } : {}),
          consent: {
            dataProcessing: true,
            profiling: Boolean(field('Profiling Consent') && field('Profiling Consent').checked),
            marketing: Boolean(field('Marketing Consent') && field('Marketing Consent').checked),
          },
          prefill,
          ...(analyticsAllowed() ? { sessionKey } : {}),
          source: state.from,
        };
        flush();
        if (button) button.disabled = true;
        // Opened during the click so that pop-up blockers allow it; WhatsApp loads once the request is saved.
        const tab = window.open('', '_blank');
        let res;
        try {
          res = await fetch(`${api}/v1/booking/requests`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
        } catch (e) {
          res = null;
        }
        if (button) button.disabled = false;
        if (!res || res.status >= 500 || res.status === 429) {
          // The concierge cannot be reached: the original WhatsApp message, so the client can still book.
          if (tab) tab.close();
          fallback();
          return;
        }
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (tab) tab.close();
          status.hidden = false;
          status.textContent =
            (json.error && json.error.message && json.error.message !== 'The request is invalid'
              ? json.error.message
              : 'Please check your details and try again.');
          track('booking_error', { step: 'details', code: `http_${res.status}` });
          return;
        }
        // Saved. The note above the button already says the team confirms the appointment (owner, 23 Oct 2026).
        status.hidden = true;
        status.replaceChildren();
        if (!json.whatsappUrl) {
          if (tab) tab.close();
          return;
        }
        if (tab) {
          tab.opener = null;
          tab.location.href = json.whatsappUrl;
        } else window.location.href = json.whatsappUrl;
      },
    };
  }

  window.ArnavBooking = { init };
})();
